import type { FormState } from "../App";
import type { TZ } from "../lib/time";
import { RELATIVE_RANGES } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface Props {
  form: FormState;
  onFormChange: (f: FormState) => void;
  onSearch: () => void;
  onReset: () => void;
  tz: TZ;
  isSearching: boolean;
}

function field(label: string, input: React.ReactNode) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs text-foreground/70">{label}</Label>
      {input}
    </div>
  );
}

export default function SearchForm({ form, onFormChange, onSearch, onReset, tz, isSearching }: Props) {
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    onFormChange({ ...form, [key]: value });

  return (
    <div className="flex flex-col gap-2">
      {/* Time range */}
      {field(
        "Time range",
        <Select value={form.timeRange ?? undefined} onValueChange={(v) => v != null && set("timeRange", v)}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.keys(RELATIVE_RANGES).map((k) => (
              <SelectItem key={k} value={k} className="text-xs">
                {k === "5m" ? "Last 5 minutes"
                  : k === "15m" ? "Last 15 minutes"
                  : k === "1h"  ? "Last 1 hour"
                  : k === "6h"  ? "Last 6 hours"
                  : k === "24h" ? "Last 24 hours"
                  : k === "7d"  ? "Last 7 days"
                  : "Last 30 days"}
              </SelectItem>
            ))}
            <SelectItem value="custom" className="text-xs">Custom (absolute)</SelectItem>
          </SelectContent>
        </Select>,
      )}

      {/* Custom date range */}
      {form.timeRange === "custom" && (
        <div className="flex flex-col gap-1">
          {field(`From (${tz === "utc" ? "UTC" : "local"})`, (
            <Input
              type="datetime-local"
              className="h-8 text-xs"
              value={form.customStart}
              onChange={(e) => set("customStart", e.target.value)}
            />
          ))}
          {field(`To (${tz === "utc" ? "UTC" : "local"})`, (
            <Input
              type="datetime-local"
              className="h-8 text-xs"
              value={form.customEnd}
              onChange={(e) => set("customEnd", e.target.value)}
            />
          ))}
        </div>
      )}

      {/* Identifier filters */}
      <div className="flex gap-1.5">
        <div className="flex-1">
          {field("ICAO hex", (
            <Input
              className="h-8 text-xs"
              placeholder="e.g. a1b2c3"
              value={form.hex}
              onChange={(e) => set("hex", e.target.value)}
            />
          ))}
        </div>
        <div className="flex-1">
          {field("Callsign", (
            <Input
              className="h-8 text-xs"
              placeholder="e.g. UAL123"
              value={form.flight}
              onChange={(e) => set("flight", e.target.value)}
            />
          ))}
        </div>
      </div>

      {field("Registration", (
        <Input
          className="h-8 text-xs"
          placeholder="e.g. N12345"
          value={form.registration}
          onChange={(e) => set("registration", e.target.value)}
        />
      ))}

      {/* Altitude */}
      <div className="flex gap-1.5">
        <div className="flex-1">
          {field("Alt min (ft)", (
            <Input
              type="number"
              className="h-8 text-xs"
              value={form.altMin}
              onChange={(e) => set("altMin", e.target.value)}
            />
          ))}
        </div>
        <div className="flex-1">
          {field("Alt max (ft)", (
            <Input
              type="number"
              className="h-8 text-xs"
              value={form.altMax}
              onChange={(e) => set("altMax", e.target.value)}
            />
          ))}
        </div>
      </div>

      {field("Squawk", (
        <Input
          className="h-8 text-xs"
          placeholder="e.g. 7700"
          value={form.squawk}
          onChange={(e) => set("squawk", e.target.value)}
        />
      ))}

      {/* Flags */}
      <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs">
        {([
          ["alert",    "Alert",    "alert"]    as const,
          ["spi",      "SPI",      "spi"]      as const,
          ["military", "Military", "military"] as const,
          ["onGround", "On ground","onGround"] as const,
        ] as [keyof FormState, string, string][]).map(([key, label, id]) => (
          <div key={id} className="flex items-center gap-1.5">
            <Checkbox
              id={id}
              checked={form[key] as boolean}
              onCheckedChange={(v) => set(key, Boolean(v))}
            />
            <label htmlFor={id} className="text-xs cursor-pointer select-none">{label}</label>
          </div>
        ))}
      </div>

      {/* Radius */}
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-foreground/70">Radius filter</Label>
        <p className="text-[0.7rem] text-muted-foreground">
          Click the map to set the center, or enter manually.
        </p>
        <div className="flex gap-1.5">
          <div className="flex-1">
            {field("Lat", (
              <Input
                type="number"
                step="0.000001"
                className="h-8 text-xs"
                value={form.lat}
                onChange={(e) => set("lat", e.target.value)}
              />
            ))}
          </div>
          <div className="flex-1">
            {field("Lon", (
              <Input
                type="number"
                step="0.000001"
                className="h-8 text-xs"
                value={form.lon}
                onChange={(e) => set("lon", e.target.value)}
              />
            ))}
          </div>
        </div>
        {field("Radius (nm)", (
          <Input
            type="number"
            min="0"
            className="h-8 text-xs"
            value={form.radiusNm}
            onChange={(e) => set("radiusNm", e.target.value)}
          />
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1 h-8 text-xs" onClick={onReset} disabled={isSearching}>
          Reset
        </Button>
        <Button className="flex-1 h-8 text-xs" onClick={onSearch} disabled={isSearching}>
          {isSearching ? (
            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Searching…</>
          ) : (
            "Search"
          )}
        </Button>
      </div>
    </div>
  );
}
